import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperType } from 'swiper';
import { Autoplay } from 'swiper/modules';
import { IoChevronBack, IoChevronForward } from 'react-icons/io5';
import { callPhone } from '../../utils/phoneUtils';
import type { ServiceCarouselProps } from './types';

const ServiceCarouselMobile: React.FC<ServiceCarouselProps> = ({ data, onBookClick }) => {
  const navigate = useNavigate();
  const swiperRef = useRef<SwiperType | null>(null);
  const allServices = data.categories.flatMap(cat => cat.services);

  return (
    <section className="py-10 sm:py-14 md:py-16 bg-white dark:bg-custom-dark">
      
      {/* Header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 mb-8 sm:mb-10 md:mb-12 text-center">
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-display font-bold text-gray-900 dark:text-white mb-3 sm:mb-4 leading-tight">
          {data.title}
        </h2>
        <p className="text-sm sm:text-base md:text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto mb-4">
          {data.subtitle}
        </p>
        
        {/* View More Link - After Description, Right-Aligned, No Background */}
        <div className="flex justify-end">
          <button
            onClick={() => navigate('/services-v2')}
            className="text-primary hover:text-primary/80 font-semibold flex items-center gap-2 transition-colors text-sm sm:text-base"
          >
            <span>View More</span>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>

      {/* Carousel Wrapper */}
      <div className="max-w-sm sm:max-w-md md:max-w-lg mx-auto px-4 relative">
        {/* Custom Previous Button */}
        <button
          onClick={() => swiperRef.current?.slidePrev()}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/20 hover:bg-white/30 backdrop-blur-sm flex items-center justify-center transition-all shadow-lg"
          aria-label="Previous slide"
        >
          <IoChevronBack className="text-white text-xl sm:text-2xl" />
        </button>

        {/* Custom Next Button */}
        <button
          onClick={() => swiperRef.current?.slideNext()}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/20 hover:bg-white/30 backdrop-blur-sm flex items-center justify-center transition-all shadow-lg"
          aria-label="Next slide"
        >
          <IoChevronForward className="text-white text-xl sm:text-2xl" />
        </button>

        <Swiper
          onSwiper={(swiper) => (swiperRef.current = swiper)}
          modules={[Autoplay]}
          spaceBetween={16}
          slidesPerView={1}
          centeredSlides={true}
          autoplay={{
            delay: 3000,
            disableOnInteraction: false,
          }}
          loop={true}
          className="service-carousel-mobile"
        >
          {allServices.map((service, index) => (
            <SwiperSlide key={index}>
              <div className="bg-[#252527] rounded-xl shadow-lg overflow-hidden flex flex-col w-full min-h-[220px] sm:min-h-[260px] md:min-h-[280px]">
                
                {/* Content */}
                <div className="p-4 sm:p-5 md:p-6 flex-1 flex flex-col">
                  
                  {/* Title */}
                  <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-white mb-3 sm:mb-4 leading-snug">
                    {service.name}
                  </h3>

                  {/* Description */}
                  <p className="text-gray-400 text-sm sm:text-base leading-relaxed line-clamp-2 sm:line-clamp-3 mb-3 sm:mb-4">
                    {service.description}
                  </p>

                  {/* Price + Duration */}
                  <div className="flex flex-wrap items-center gap-3 sm:gap-4">
                    <div className="text-xl sm:text-2xl md:text-3xl font-bold text-primary">
                      {service.price}
                    </div>

                    <div className="flex items-center gap-2 text-gray-300 text-xs sm:text-sm">
                      <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>{service.duration}</span>
                    </div>
                  </div>
                </div>

                {/* CTA Button */}
                <button
                  onClick={onBookClick || (() => callPhone())}
                  className="w-full bg-primary hover:bg-primary/90 active:scale-[0.98] text-white font-semibold py-3 sm:py-4 px-4 sm:px-6 flex items-center justify-center gap-2 sm:gap-3 transition-all text-sm sm:text-base"
                >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <span>Book Now</span>
                </button>

              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </section>
  );
};

export default ServiceCarouselMobile;
